<script setup lang="ts">
/**
 * A single source<->target row rendered as two equal columns (Figma node 1791:99913):
 * left is the purple index badge + read-only source text, right is either the editable target
 * textarea (custom) or read-only target text (premade / non-editable). The row owns NO border;
 * the parent editor wraps it in a "comparison card".
 */
import { computed } from "vue";
import NovaTextarea from "@/ui-kit/NovaTextarea/NovaTextarea.vue";
import DiffText from "@/features/experience-shared/components/DiffText.vue";
import type { TranslationRowDiff } from "@/features/experience-shared/highlights-v2/translation/types/highlight-translation";

const props = defineProps<{
  index: number;
  source: string;
  target: string;
  editable?: boolean;
  readonly?: boolean;
  rowId: string;
  diff?: TranslationRowDiff;
  error?: string;
}>();

defineEmits<{
  (e: "update:translation", value: string): void;
}>();

// Map the diff status onto DiffText's (oldValue -> value) word diff: added -> "" vs source
// (all green), removed -> source vs "" (all red), modified -> previous vs current source.
const diffValue = computed(() => (props.diff?.status === "removed" ? "" : props.source));
const diffOldValue = computed(() => {
  if (props.diff?.status === "added") return "";
  if (props.diff?.status === "modified") return props.diff.previousSource;
  return props.source;
});
</script>

<template>
  <div class="flex w-full items-start gap-3" data-testid="translation-highlight-row">
    <!-- Left column: index badge + read-only source. -->
    <div class="flex min-w-0 flex-1 items-start gap-2">
      <span
        class="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-nova-info-2-tint px-1 text-center font-bold text-[12px] leading-4 text-nova-info-2-strong"
        data-testid="translation-highlight-index"
      >
        {{ index }}
      </span>

      <p
        class="m-0 min-w-0 flex-1 whitespace-pre-line break-words text-[14px] leading-[19px] text-nova-default"
        data-testid="translation-highlight-source"
      >
        <DiffText v-if="diff != null" :value="diffValue" :old-value="diffOldValue" />
        <template v-else>{{ source }}</template>
      </p>
    </div>

    <!-- Right column: editable target (custom) or read-only target text. -->
    <div class="min-w-0 flex-1" data-testid="translation-highlight-target">
      <NovaTextarea
        v-if="editable && !readonly"
        :id="`translation-${rowId}`"
        :model-value="target"
        size="sm"
        :rows="1"
        autoresize
        :error="error ? $t(error) : undefined"
        @update:model-value="$emit('update:translation', $event)"
      />
      <p v-else class="m-0 whitespace-pre-line break-words text-[14px] leading-[19px] text-nova-default">
        {{ target || "-" }}
      </p>
    </div>
  </div>
</template>
